import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  MessageCircle,
  Loader2,
  CalendarRange,
  BookOpen,
  CreditCard,
  ShieldCheck,
  ChevronDown,
  Info,
  Moon,
  Sunset,
} from "lucide-react";
import InternalNav from "@/components/InternalNav";
import { useContent } from "@/lib/ContentProvider";

/**
 * Carte compacte à accordéon pour la section "Réserver en toute sérénité".
 * Résumé toujours visible, détails révélés au clic sur "En savoir plus".
 */
function InfoCard({ Icon, title, summary, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={testid}
      className="bg-white/85 backdrop-blur-sm border border-refuge-border rounded-2xl p-4 md:p-5 transition-all"
      style={{ boxShadow: "0 10px 24px -20px rgba(45,40,37,0.35)" }}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(232, 184, 189, 0.18)" }}>
          <Icon className="w-4 h-4 text-refuge-ruby" strokeWidth={1.3} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base md:text-[17px] font-light text-refuge-text leading-snug mb-1">{title}</p>
          <p className="font-body text-[13px] md:text-sm text-refuge-text/80 leading-relaxed">{summary}</p>
          {children && (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              data-testid={`${testid}-toggle`}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-refuge-ruby/30 bg-refuge-ruby/8 font-body text-[10.5px] tracking-[0.16em] uppercase text-refuge-ruby hover:bg-refuge-ruby hover:text-white hover:border-refuge-ruby transition-all"
              style={{ background: open ? "var(--cr-ruby, #8b1e3f)" : "rgba(139, 30, 63, 0.06)", color: open ? "#fff" : undefined }}
            >
              {open ? "Réduire" : "En savoir plus"}
              <ChevronDown
                size={11}
                strokeWidth={1.8}
                className="transition-transform duration-300"
                style={{ transform: open ? "rotate(180deg)" : "rotate(0)" }}
              />
            </button>
          )}
        </div>
      </div>
      {children && (
        <div
          className="grid transition-[grid-template-rows,opacity] duration-500 ease-out"
          style={{ gridTemplateRows: open ? "1fr" : "0fr", opacity: open ? 1 : 0 }}
        >
          <div className="overflow-hidden">
            <div className="pl-12 pr-1 pt-3 text-[13px] md:text-sm text-refuge-text/85 font-body leading-relaxed space-y-2.5">
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Reserver() {
  const { contact: CONTACT, pricing: PRICING, buildWhatsAppUrl } = useContent();
  const LODGIFY = CONTACT.lodgifyUrl;
  const [loaded, setLoaded] = useState(false);

  const perNight = PRICING?.baseNight ?? 150;
  const weekendTotal = perNight * (PRICING?.weekendNights ?? 2);

  return (
    <div data-testid="reserver-page" className="bg-refuge-bg min-h-screen">
      <InternalNav />

      {/* HEADER */}
      <section className="pt-32 md:pt-36 pb-6 md:pb-8 bg-refuge-bg">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-3">Réserver</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-refuge-text leading-[1.05]">
            Choisissez <span className="italic">vos dates</span>
          </h1>
        </div>
      </section>

      {/* RÉSERVER EN TOUTE SÉRÉNITÉ — 4 cartes compactes */}
      <section className="pb-8 md:pb-10 bg-refuge-bg">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-6 md:mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-refuge-text leading-tight">
              Réserver <span className="italic">en toute sérénité</span>
            </h2>
            <p className="font-body text-sm text-refuge-soft italic mt-2 max-w-2xl mx-auto">
              Quelques informations essentielles pour choisir vos dates et réserver simplement, en toute transparence.
            </p>

            {/* Tarifs — chips discrets, alignés sur les valeurs de /tarifs */}
            <div className="mt-5 inline-flex flex-wrap items-center justify-center gap-2.5" data-testid="reserver-tarifs">
              <span
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-refuge-border font-body text-[12px] md:text-sm text-refuge-text"
                data-testid="reserver-tarif-semaine"
              >
                <Moon className="w-3.5 h-3.5 text-refuge-ruby" strokeWidth={1.4} />
                <span className="tracking-[0.14em] uppercase text-[10.5px] text-refuge-soft">Semaine</span>
                <span className="font-heading text-refuge-text">{perNight} €</span>
                <span className="text-refuge-soft italic">/ nuit</span>
              </span>
              <span
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-refuge-border font-body text-[12px] md:text-sm text-refuge-text"
                data-testid="reserver-tarif-weekend"
              >
                <Sunset className="w-3.5 h-3.5 text-refuge-ruby" strokeWidth={1.4} />
                <span className="tracking-[0.14em] uppercase text-[10.5px] text-refuge-soft">Week-end</span>
                <span className="font-heading text-refuge-text">{weekendTotal} €</span>
                <span className="text-refuge-soft italic">/ 2 nuits</span>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="reserver-cards">
            <InfoCard
              testid="card-periodes"
              Icon={CalendarRange}
              title="Périodes & séjours"
              summary="Une nuit possible en semaine. Deux nuits minimum le week-end."
            >
              <p>Une nuit peut être réservée en semaine, selon les périodes.</p>
              <p>Les séjours du week-end sont proposés avec un minimum de deux nuits.</p>
            </InfoCard>

            <InfoCard
              testid="card-calendrier"
              Icon={BookOpen}
              title="Comment lire le calendrier"
              summary="Gras : arrivée possible · Gris clair : séjour ou départ · Barré : indisponible"
            >
              <p><span className="font-medium text-refuge-text">Dates en gras</span> — disponibles pour commencer un séjour. Sélectionnables comme date d'arrivée.</p>
              <p><span className="font-medium text-refuge-text">Dates gris clair</span> — un séjour ne peut pas commencer ici. En revanche, cette date peut faire partie d'un séjour déjà commencé ou correspondre à votre départ.</p>
              <p><span className="font-medium text-refuge-text">Dates barrées</span> — ces nuits ne sont pas disponibles à la réservation.</p>
              <p className="italic">Pour réserver : choisir d'abord une date d'arrivée en gras, puis la date de départ.</p>
              <div
                className="mt-3 flex items-start gap-2 p-3 rounded-xl border border-refuge-ruby/25"
                style={{ background: "rgba(232, 184, 189, 0.14)" }}
              >
                <Info className="w-3.5 h-3.5 text-refuge-ruby shrink-0 mt-[3px]" strokeWidth={1.5} />
                <div>
                  <p className="font-heading text-[13px] text-refuge-text mb-1">Besoin de modifier votre recherche ?</p>
                  <p className="text-[12.5px] leading-relaxed">
                    Pensez à effacer les dates affichées en haut du calendrier avant d'en choisir de nouvelles. Sans cette étape, le calendrier peut sembler entièrement indisponible alors qu'il ne l'est pas.
                  </p>
                </div>
              </div>
            </InfoCard>

            <InfoCard
              testid="card-paiement"
              Icon={CreditCard}
              title="Réservation & paiement"
              summary="50 € à la réservation · Solde prélevé 7 jours avant l'arrivée"
            >
              <p>Un acompte de <span className="font-medium text-refuge-text">50 €</span> est demandé au moment de la réservation pour réserver définitivement les dates choisies.</p>
              <p>Cet acompte est non remboursable — il garantit l'exclusivité des dates dès la confirmation.</p>
              <p>Le <span className="font-medium text-refuge-text">solde restant</span> est automatiquement prélevé 7 jours avant l'arrivée.</p>
              <p>Si vous devez annuler, faites-le avant cette échéance pour éviter le prélèvement automatique. Une fois prélevé, le solde devient également non remboursable.</p>
            </InfoCard>

            <InfoCard
              testid="card-caution"
              Icon={ShieldCheck}
              title="Caution bancaire"
              summary="250 € temporairement réservés · Aucun débit"
            >
              <p>Une caution de <span className="font-medium text-refuge-text">250 €</span> est demandée sous forme de <span className="italic">préautorisation bancaire</span>.</p>
              <p>Ce n'est pas un paiement. Aucune somme n'est débitée du compte. Votre banque peut simplement signaler que 250 € sont temporairement réservés — comme à l'hôtel ou pour la location d'un véhicule.</p>
              <p>La somme n'est utilisée qu'en cas de dommages constatés durant le séjour.</p>
              <p>En l'absence de problème, la préautorisation est <span className="font-medium text-refuge-text">automatiquement levée 4 jours après le départ</span>, sans aucune démarche.</p>
            </InfoCard>
          </div>
        </div>
      </section>

      {/* CALENDRIER LODGIFY */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-3 md:px-6">
          <div
            className="relative overflow-hidden bg-white border border-refuge-border rounded-2xl"
            style={{ minHeight: "880px" }}
            data-testid="lodgify-container"
          >
            {!loaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10 bg-refuge-bg">
                <Loader2 size={28} className="animate-spin text-refuge-ruby" />
                <p className="font-heading italic text-xl text-refuge-soft">Préparation du calendrier…</p>
              </div>
            )}
            <iframe
              src={LODGIFY}
              title="Réservation Curieux Refuge"
              data-testid="lodgify-iframe"
              onLoad={() => setLoaded(true)}
              loading="lazy"
              className="w-full"
              style={{ minHeight: "880px", height: "880px", border: "none", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* Question WhatsApp — compact */}
      <section className="bg-refuge-bg pb-12 md:pb-16 text-center">
        <a
          href={buildWhatsAppUrl("Bonjour, j'ai une question sur la réservation du Curieux Refuge.")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cta-whatsapp"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-refuge-text text-refuge-text font-body text-sm hover:bg-refuge-text hover:text-white transition-all"
        >
          <MessageCircle size={15} strokeWidth={1.4} /> J'ai une question
        </a>
      </section>

      <div className="bg-refuge-bg pb-10 text-center">
        <Link
          to="/accueil"
          data-testid="back-to-hub"
          className="inline-flex items-center gap-2 font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} /> Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}
